import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useFetchValueReferenceDefaults,
  useFetchValueReferenceOverrides,
  useCreateValueReferenceOverride,
  useUpdateValueReferenceOverride,
  useDeleteValueReferenceOverride
} from "src/queries/hooks/valueReferences";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { computeOverrideOps, mergeOverrides, setOrDropEdit } from "./AnalyticsSettings.utils";

const ID_FIELD = "referenceId";
const VALUE_FIELD = "value";
const DEFAULT_VALUE_FIELD = "defaultValue";

const useValueReferences = (assistantId, setFeedbackConfig) => {
  const queryClient = useQueryClient();

  const [editedValueReferences, setEditedValueReferences] = useState({});
  const [isPublishing, setIsPublishing] = useState(false);

  const { data: defaults, isLoading: isDefaultsLoading } = useFetchValueReferenceDefaults();
  const { data: overrides, isLoading: isOverridesLoading } = useFetchValueReferenceOverrides(assistantId);

  const createOverrideMutation = useCreateValueReferenceOverride();
  const updateOverrideMutation = useUpdateValueReferenceOverride();
  const deleteOverrideMutation = useDeleteValueReferenceOverride();

  const handleValueReferenceUpdate = (referenceId, value) => {
    const defaultRef = defaults?.find((d) => d.referenceId === referenceId);
    const override = overrides?.find((o) => o.referenceId === referenceId);
    const originalValue = override?.value ?? defaultRef?.value;
    setOrDropEdit(setEditedValueReferences, referenceId, value, originalValue);
  };

  const valueReferences = defaults && overrides
    ? mergeOverrides({
      defaults,
      overrides,
      edits: editedValueReferences,
      idField: ID_FIELD,
      valueField: VALUE_FIELD,
      defaultValueField: DEFAULT_VALUE_FIELD
    })
    : undefined;

  const areSettingsChanged = Object.keys(editedValueReferences).length > 0;
  const isLoading = isDefaultsLoading || isOverridesLoading;

  const clear = () => {
    setEditedValueReferences({});
  };

  const publish = async () => {
    setIsPublishing(true);
    try {
      const ops = computeOverrideOps({
        edits: editedValueReferences,
        defaults,
        overrides,
        mutations: {
          create: createOverrideMutation,
          update: updateOverrideMutation,
          delete: deleteOverrideMutation
        },
        idField: ID_FIELD,
        valueField: VALUE_FIELD,
        basePayload: { assistantId }
      });
      const results = await Promise.allSettled(ops);
      const hasFailures = results.some((r) => r.status === "rejected");

      await queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.VALUE_REFERENCE_OVERRIDES] });

      if (hasFailures) {
        const firstError = results.find((r) => r.status === "rejected")?.reason;
        console.error("Error occurred while saving value references", firstError);
        setFeedbackConfig?.({
          show: true,
          message: "Some value reference changes failed to save. Please try again.",
          severity: "error"
        });
        return false;
      }

      setEditedValueReferences({});
      return true;
    } catch (e) {
      console.error("Error occurred while saving value references", e);
      setFeedbackConfig?.({
        show: true,
        message: "An error occurred while saving value references. Please try again.",
        severity: "error"
      });
      return false;
    } finally {
      setIsPublishing(false);
    }
  };

  return {
    valueReferences,
    areSettingsChanged,
    isLoading,
    isPublishing,
    onValueReferenceUpdate: handleValueReferenceUpdate,
    publish,
    clear
  };
};

export default useValueReferences;
